import { Capacitor } from '@capacitor/core';
import { load } from "@cashfreepayments/cashfree-js";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  CheckCircle, 
  AlertCircle, 
  ArrowRight,
  Coins,
  Lock,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Zap,
  RefreshCw,
  Clock,
  ChevronLeft,
  Link2,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CREDIT_PACKS, getPackGrantCredits } from '../../config/creditPacks';
import type {CreditPack} from '../../config/creditPacks';
import { supabase } from '../../lib/supabase';
import { logEvent } from '../../services/loggingService';
import { appLogger } from '../../utils/appLogger';
import { formatPackPrice, getPackPrice } from '../../utils/localePrice';
import { GGJLogoFull } from '../Brand/GGJLogo';
import { goToCreditCheckout } from '../../utils/creditCheckoutNavigation';
import { trackInitiateCheckout, trackPurchase } from '../../utils/metaPixel';
import { getCashfreeSdkMode } from './creditCheckoutUtils';

type DisplayCurrency = 'INR' | 'USD';
const USD_INR_DISPLAY_RATE = 83.12;

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete?: (transactionId: string, credits: number) => void;
  /** Pack id (e.g. pack-monthly) or pricing page id (monthly, quarterly, halfyearly) */
  initialPackId?: string;
}

const PRICING_PAGE_PACK_MAP: Record<string, string> = {
  monthly: 'pack-monthly',
  quarterly: 'pack-quarterly',
  halfyearly: 'pack-halfyearly',
};

function resolvePackFromId(id: string | undefined): CreditPack | null {
  if (!id) return null;
  const packId = PRICING_PAGE_PACK_MAP[id] ?? id;
  return CREDIT_PACKS.find((p) => p.id === packId) ?? null;
}

type PaymentMethod = 'cashfree' | 'phonepe' | 'qrcode';

// Cashfree SDK mode: localhost → sandbox; production host → live (matches edge functions).
const CASHFREE_SDK_MODE = getCashfreeSdkMode();

const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  isOpen,
  onClose,
  onPurchaseComplete,
  initialPackId,
}) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<'select-plan' | 'payment-method' | 'processing' | 'success' | 'error'>('select-plan');
  const [selectedPlan, setSelectedPlan] = useState<CreditPack | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [processingElapsedSeconds, setProcessingElapsedSeconds] = useState(0);
  const processingStartRef = useRef<number | null>(null);
  const purchaseTrackedRef = useRef(false);

  const [cashfree, setCashfree] = useState<any>(null);
  const [billingOpen, setBillingOpen] = useState(false); // collapsed on small screens to avoid scroll
  const [confirmPurchase, setConfirmPurchase] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>('INR');
  const [saveCheckoutInfo, setSaveCheckoutInfo] = useState(true);
  const [cardholderName, setCardholderName] = useState('');

  // User data for payment and billing (prefilled from profile)
  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    billingAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  // Cost-aware packs from config (credits = optimizations; AI/model cost accounted for)
  const plans = CREDIT_PACKS;

  // Reset state when modal opens; clear stale payment flag when modal closes
  useEffect(() => {
    if (isOpen) {
      setStep('select-plan');
      let pendingId = initialPackId;
      if (!pendingId && typeof sessionStorage !== 'undefined') {
        pendingId = sessionStorage.getItem('pricing_pending_plan') ?? undefined;
        if (pendingId) sessionStorage.removeItem('pricing_pending_plan');
      }
      const fromProp = resolvePackFromId(pendingId);
      const defaultPlan = Capacitor.isNativePlatform()
        ? (fromProp ?? null)
        : (fromProp ?? CREDIT_PACKS.find((p) => p.popular) ?? CREDIT_PACKS[0] ?? null);
      setSelectedPlan(defaultPlan);
      setError(null);
      setTransactionId(null);
      setProcessingElapsedSeconds(0);
      processingStartRef.current = null;
      purchaseTrackedRef.current = false;
      setConfirmPurchase(false);
      setDisplayCurrency('INR');
      setSaveCheckoutInfo(true);
      setCardholderName('');
      setBillingOpen(false);
    } else {
      // Modal closed without completing payment — clear flag so future sign-ins aren't blocked
      try { localStorage.removeItem('payment_in_progress'); } catch (_) {}
    }
  }, [isOpen, initialPackId]);

  // Web uses dedicated /checkout URL — redirect instead of overlay modal
  useEffect(() => {
    if (!isOpen || Capacitor.isNativePlatform()) return;
    onClose();
    goToCreditCheckout(initialPackId);
  }, [isOpen, initialPackId, onClose]);

  // Meta Pixel: track Purchase when credit purchase succeeds (once per success step)
  useEffect(() => {
    if (step === 'success' && selectedPlan && !purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      trackPurchase(selectedPlan.price, 'INR', transactionId || undefined);
    }
  }, [step, selectedPlan, transactionId]);

  // Start processing timer when entering processing step
  useEffect(() => {
    if (step === 'processing') {
      processingStartRef.current = Date.now();
      setProcessingElapsedSeconds(0);
    }
  }, [step]);

  // Tick elapsed time while processing
  useEffect(() => {
    if (step !== 'processing') return;
    const interval = setInterval(() => {
      const start = processingStartRef.current;
      if (start != null) setProcessingElapsedSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

    // Fetch user data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);
  
  
  // Initialize Cashfree SDK when component mounts
  useEffect(() => {
    const initializeSDK = async () => {
      try {
        const cf = await load({ mode: CASHFREE_SDK_MODE });
        setCashfree(cf);
        appLogger.debug('Cashfree SDK initialized', { mode: CASHFREE_SDK_MODE });
      } catch (error) {
        appLogger.error('Cashfree initialization failed', error);
        setError(t('creditsPurchaseModal.paymentUnavailable'));
      }
    };

    initializeSDK();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user data for payment and billing (prefill from profile)
  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('users')
          .select('name, phone, street_address, city, state, zip_code, country')
          .eq('user_id', user.id)
          .single();

        const fullName = userProfile?.name || user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0] || '';
        const nameParts = (fullName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        setUserData({
          email: user.email || '',
          phone: userProfile?.phone || (user.phone as string) || '',
          firstName,
          lastName,
          billingAddress: userProfile?.street_address || '',
          city: userProfile?.city || '',
          state: userProfile?.state || '',
          zipCode: userProfile?.zip_code || '',
          country: userProfile?.country || '',
        });
        setCardholderName([firstName, lastName].filter(Boolean).join(' '));
      }
    } catch (error) {
      appLogger.error('Error fetching user data', error);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const updateBilling = (field: keyof typeof userData, value: string) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  // Format a plan's competitive local price (not an INR conversion)
  const formatCurrency = (packId: string) =>
    formatPackPrice(packId, i18n.language || 'en');

  // GST logic: India = 18% GST (CGST 9% + SGST 9%).
  // Exports to other countries are zero-rated under Indian GST law.
  const isIndianCustomer =
    !userData.country || /^(india|in)$/i.test(userData.country.trim());
  const gstRatePct = isIndianCustomer ? 18 : 0;

  const calcGST = (basePrice: number) => {
    const gst = parseFloat((basePrice * gstRatePct / 100).toFixed(2));
    return { gst, total: parseFloat((basePrice + gst).toFixed(2)) };
  };

  const fmtINR = (n: number) => `₹${n.toFixed(2)}`;
  const fmtINRDisplay = (n: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);
  const fmtUSDDisplay = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

  const formatPlanDisplayPrice = (plan: CreditPack) =>
    displayCurrency === 'INR' ? fmtINRDisplay(plan.price) : formatPackPrice(plan.id, 'es');

  const formatCheckoutTotal = (plan: CreditPack) => {
    const { total } = calcGST(plan.price);
    if (displayCurrency === 'INR') return fmtINRDisplay(total);
    const usdBase = getPackPrice(plan.id, 'es').amount;
    const usdGst = isIndianCustomer ? usdBase * gstRatePct / 100 : 0;
    return fmtUSDDisplay(usdBase + usdGst);
  };

  // Select plan only; native uses "Proceed to Payment", web uses single-page checkout
  const handleSelectPlan = (plan: CreditPack) => {
    setSelectedPlan(plan);
    if (!Capacitor.isNativePlatform()) {
      trackInitiateCheckout(plan.price, 'INR');
    }
    logEvent({
      level: 'info',
      action: 'credit_plan_selected',
      details: { planId: plan.id, credits: getPackGrantCredits(plan), price: plan.price }
    });
  };

  // Proceed from plan selection to payment step (CTA button)
  const handleProceedToPayment = () => {
    if (!selectedPlan) return;
    trackInitiateCheckout(selectedPlan.price, 'INR');
    setStep('payment-method');
    logEvent({
      level: 'info',
      action: 'credit_proceed_to_payment',
      details: { planId: selectedPlan.id, credits: getPackGrantCredits(selectedPlan), price: selectedPlan.price }
    });
  };

  const applyCardholderToBilling = () => {
    if (!cardholderName.trim()) return;
    const parts = cardholderName.trim().split(/\s+/);
    setUserData((prev) => ({
      ...prev,
      firstName: parts[0] || prev.firstName,
      lastName: parts.slice(1).join(' ') || prev.lastName,
    }));
  };

  const handleLinkCheckout = () => {
    if (!selectedPlan || !userData.email.trim()) return;
    applyCardholderToBilling();
    setConfirmPurchase(true);
    processPayment('cashfree');
    logEvent({
      level: 'info',
      action: 'link_checkout_clicked',
      details: { planId: selectedPlan.id },
    });
  };

  // Proceed to Cashfree checkout (payment method is chosen on Cashfree's page)
  const handlePayAndUpgrade = () => {
    if (!selectedPlan || !userData.email.trim()) return;
    if (Capacitor.isNativePlatform() && !confirmPurchase) return;
    applyCardholderToBilling();
    processPayment('cashfree');
    logEvent({
      level: 'info',
      action: 'pay_and_upgrade_clicked',
      details: { planId: selectedPlan.id }
    });
  };

 // Process payment (simulated)
 const processPayment11 = async (method: PaymentMethod) => {
  if (!selectedPlan) return;

  setStep('processing');
  setLoading(true);
  setError(null);

  try {
    // Force-refresh the session so the token is always fresh (prevents Invalid JWT / missing sub errors)
    const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
    const session = refreshed?.session ?? (await supabase.auth.getSession()).data.session;
    if (refreshErr) appLogger.debug('Session refresh warning (non-fatal):', refreshErr.message);
    if (!session) {
      throw new Error('Authentication required. Please sign in to continue.');
    }

    // Prepare payment data
    const paymentData = {
      amount: selectedPlan.price,
      customerEmail: userData.email,
      customerPhone: userData.phone || '9999999999',
      planId: selectedPlan.id,
      credits: getPackGrantCredits(selectedPlan),
      locale: i18n.language || 'en',
      country: userData.country || '',
    };

    // Call the Supabase Edge Function with the freshly-refreshed user JWT
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-cashfree-order`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData)
    });

    // First, check if the response is OK
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create payment order');
    }

    // Parse the response carefully
    const data = await response.json();
    appLogger.debug('Payment API response', data);

    // Debug: log all keys in the response
    appLogger.debug('Response keys', Object.keys(data));

    // Extract order ID - checking multiple possible fields
    const receivedOrderId = data.order_id || data.cf_order_id || data.orderId;
    if (!receivedOrderId) {
      appLogger.error('Order ID missing in response', data);
      throw new Error('Order ID not received from payment gateway');
    }

    // Extract session ID - checking multiple possible fields
    const sessionId = data.payment_session_id || data.session_id;
    if (!sessionId) {
      appLogger.error('Session ID missing in response', data);
      throw new Error('Payment session ID not received');
    }

    // Now use these values
    setTransactionId(receivedOrderId);
    appLogger.debug('Payment session initialized', {
      orderId: receivedOrderId,
      sessionId: sessionId
    });

    // Log the payment initiation
    await logEvent({
      level: 'info',
      action: 'payment_initiated',
      details: {
        planId: selectedPlan.id,
        credits: getPackGrantCredits(selectedPlan),
        price: selectedPlan.price,
        paymentMethod: method,
        orderId: data.order_id
      }
    });

    // Initialize Cashfree SDK
    const cashfree = await load({ mode: CASHFREE_SDK_MODE });

    // Try popup first
    try {
      const popup = window.open('', 'CF_Popup', 'width=600,height=700');
     if (popup) {
      cashfree.checkout({
        paymentSessionId: data.session_id,
        redirectTarget: "popup",
        popupFeatures: "width=600,height=700",
        onSuccess: () => verifyPaymentStatus(orderId),
        onFailure: (error) => {
          appLogger.error('Payment failed', error);
          setError(error.message || 'Payment failed');
          setStep('error');
        }
      });
      return;
    }
    } catch (popupError) {
      appLogger.warn('Popup failed, falling back to tab', popupError);
    }

    // 6. Fallback to new tab
    const paymentUrl = `https://${CASHFREE_SDK_MODE === 'production' ? 'www' : 'sandbox'}.cashfree.com/pg/redirection/#/${sessionId}`;
    window.open(paymentUrl, '_blank');
    
  } catch (err) {
    appLogger.error('Payment processing error', err);
    setError(err instanceof Error ? err.message : 'Payment processing failed');
    setStep('error');
    
    await logEvent({
      level: 'error',
      action: 'payment_processing_error',
      details: {
        planId: selectedPlan?.id,
        paymentMethod: method,
        error: err instanceof Error ? err.message : String(err)
      },
      error: err instanceof Error ? err : new Error(String(err))
    });
  } finally {
    setLoading(false);
  }
};

  // Process payment — uses Cashfree's in-app modal (no external browser, no redirect)
  const processPayment = async (method: PaymentMethod) => {
    if (!selectedPlan) return;

    setStep('processing');
    setLoading(true);
    setError(null);

    try {
      // 1. Force-refresh the session to get a guaranteed fresh user JWT
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr) appLogger.debug('Session refresh warning (non-fatal):', refreshErr.message);
      const session = refreshed?.session ?? (await supabase.auth.getSession()).data.session;
      if (!session?.access_token) {
        throw new Error('Authentication required. Please sign in to continue.');
      }

      // 2. Prepare payment data
      const paymentData = {
        amount: selectedPlan.price,
        customerEmail: userData.email,
        customerPhone: userData.phone || '9999999999',
        planId: selectedPlan.id,
        credits: getPackGrantCredits(selectedPlan),
        locale: i18n.language || 'en',
        country: userData.country || '',
        billing: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          address: userData.billingAddress,
          city: userData.city,
          state: userData.state,
          zipCode: userData.zipCode,
          country: userData.country,
        },
        // Token in body as fallback when WebView gateway strips Authorization header
        access_token: session.access_token,
      };

      appLogger.debug('Creating Cashfree order', { planId: selectedPlan.id, amount: selectedPlan.price });

      // 3. Raw fetch with explicit Authorization header (avoids Supabase client WebView gateway issues)
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-cashfree-order`;
      const rawResp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (!rawResp.ok) {
        let errMsg = 'Failed to create payment order';
        try { const e = await rawResp.json(); errMsg = e.error || e.details || errMsg; } catch (_) {}
        appLogger.error('create-cashfree-order error', { status: rawResp.status, errMsg });
        throw new Error(errMsg);
      }

      const data = await rawResp.json();

      const sessionId = (data.payment_session_id || data.session_id || '').trim();
      appLogger.debug('RAW session_id from edge function:', sessionId);
      appLogger.debug('session_id last 20 chars:', sessionId.slice(-20));
      const orderId = (data.order_id || data.cf_order_id || '').trim();

      appLogger.debug('create-cashfree-order response', { sessionId, orderId });

      if (!sessionId) throw new Error('Payment session ID not received from gateway.');
      if (!orderId) throw new Error('Order ID not received from gateway.');

      setTransactionId(orderId);

      try { localStorage.setItem('payment_in_progress', 'true'); } catch (_) {}

      // 4. Load Cashfree SDK and open in-app modal — works identically on web and native WebView.
      //    _modal renders an overlay inside the current page, so the user never leaves the app.
      //    UPI deep links are handled by Android intent system from within the WebView.
      const cfInstance = await load({ mode: CASHFREE_SDK_MODE });

      appLogger.debug('Cashfree SDK loaded, opening modal checkout', { orderId, mode: CASHFREE_SDK_MODE });

      const planSnapshot = selectedPlan;
      const handlePaymentSuccess = () => {
        try { localStorage.removeItem('payment_in_progress'); } catch (_) {}
        setTransactionId(orderId);
        setStep('success');
        // Trigger usePlanStatus staggered refresh so credits update in the nav bar
        // without requiring a page reload (usePlanStatus listens to this event).
        try { window.dispatchEvent(new Event('planStatusRefresh')); } catch (_) {}
        if (onPurchaseComplete && planSnapshot) {
          onPurchaseComplete(orderId, getPackGrantCredits(planSnapshot));
        }
        logEvent({
          level: 'info',
          action: 'payment_completed',
          details: { orderId, amount: planSnapshot?.price }
        });
      };

      // Use _modal for both web and native.
      // - _modal renders an iframe overlay in the current page (no external browser, no redirect).
      // - _self was tried for native but Cashfree's hosted checkout page validates the referring
      //   origin against their domain whitelist. https://localhost (Capacitor's origin) is not
      //   in that whitelist → payment_session_id_invalid. _modal bypasses this check.
      // - "Unrecognized feature: payment" in WebView is a Permissions-Policy warning for Chrome's
      //   Payment Request API; it does NOT affect the Cashfree _modal iframe flow.
      const result = await cfInstance.checkout({
        paymentSessionId: sessionId,
        redirectTarget: '_modal',
      });

      appLogger.debug('Cashfree checkout result', result);

      if (result?.error) {
        try { localStorage.removeItem('payment_in_progress'); } catch (_) {}
        throw new Error(result.error.message || 'Payment was not completed.');
      }

      handlePaymentSuccess();
      return;

    } catch (err) {
      appLogger.error('Payment processing error', err);
      setError(err instanceof Error ? err.message : 'Payment processing failed');
      setStep('error');
      try { localStorage.removeItem('payment_in_progress'); } catch (_) {}

      await logEvent({
        level: 'error',
        action: 'payment_processing_error',
        details: {
          planId: selectedPlan?.id,
          paymentMethod: method,
          error: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined
        },
        error: err instanceof Error ? err : new Error(String(err))
      });
    } finally {
      setLoading(false);
    }
  };
  // Try again after error
  const handleTryAgain = () => {
    setStep('select-plan');
    setSelectedPlan(null);
    setError(null);
  };

  // Close modal and reset state
  const handleClose = () => {
    onClose();
    window.setTimeout(() => {
      setStep('select-plan');
      setSelectedPlan(null);
      setError(null);
    }, 300);
  };

  const isNative = Capacitor.isNativePlatform();

  // Web checkout lives at /checkout
  if (!isNative) return null;

  // Full-page overlay – native only
  const overlay = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 40 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'var(--vscode-bg-primary)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div className="flex flex-col h-full">
            <motion.div
              initial={{ scale: 1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col flex-1 min-h-0 w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Native header */}
              <div
                  style={{
                    background: 'var(--brand-popup-header)',
                    paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
                    paddingBottom: 22, paddingLeft: 20, paddingRight: 20,
                    display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{
                      background: 'rgba(255,255,255,0.18)', border: 'none',
                      borderRadius: 10, width: 36, height: 36,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent',
                    }}
                    aria-label="Go back"
                  >
                    <ChevronLeft size={20} color="white" />
                  </button>
                  <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: 20, fontWeight: 800, color: 'white', margin: 0, letterSpacing: '-0.02em' }}>
                      Subscription & Credits
                    </h1>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.70)', margin: '3px 0 0' }}>
                      Monthly · Quarterly · Half-Yearly · Cancel anytime
                    </p>
                  </div>
                </div>

              <div className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain px-3 sm:px-4 lg:px-6 pb-4 sm:pb-6">
                {step === 'select-plan' && (
                    /* ── Native: full-width rich plan cards ── */
                    <div style={{ paddingTop: 16, paddingBottom: 8 }}>
                      <p style={{ fontSize: 13, color: 'var(--vscode-text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
                        Optimizations are added instantly after payment. Cancel anytime from your dashboard.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {plans.map((plan) => {
                          const isSelected = selectedPlan?.id === plan.id;
                          const subtitle =
                            plan.id === 'pack-monthly'    ? 'Active job hunt · 60 optimizations/mo' :
                            plan.id === 'pack-quarterly'  ? 'Planned search · 180 total optimizations' :
                                                            'Long-term · 360 total optimizations';
                          const cardFeatures: { label: string; ok: boolean }[] =
                            plan.id === 'pack-monthly' ? [
                              { label: '60 resume optimizations', ok: true },
                              { label: 'Premium AI · priority queue', ok: true },
                              { label: 'PDF export + cover letter', ok: true },
                              { label: 'Full optimization history', ok: true },
                            ] :
                            plan.id === 'pack-quarterly' ? [
                              { label: '180 optimizations (60/mo)', ok: true },
                              { label: 'Premium AI · priority queue', ok: true },
                              { label: 'PDF export + cover letter', ok: true },
                              { label: 'Save 28% vs monthly', ok: true },
                            ] : [
                              { label: '360 optimizations (60/mo)', ok: true },
                              { label: 'Premium AI · priority queue', ok: true },
                              { label: 'PDF export + cover letter', ok: true },
                              { label: 'Save 28% — max savings', ok: true },
                            ];
                          const { gst: cardGst, total: cardTotal } = calcGST(plan.price);
                          return (
                            <div key={plan.id} style={{ position: 'relative' }}>
                              {plan.popular && (
                                <div style={{
                                  position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                                  zIndex: 2, background: 'var(--brand-accent-bg)',
                                  border: '1px solid var(--brand-accent-border)',
                                  color: 'var(--vscode-primary)', fontSize: 10, fontWeight: 800,
                                  padding: '3px 16px', borderRadius: 20,
                                  whiteSpace: 'nowrap', letterSpacing: '0.06em',
                                }}>
                                  MOST POPULAR
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => handleSelectPlan(plan)}
                                style={{
                                  width: '100%', padding: '18px',
                                  borderRadius: 20,
                                  border: isSelected ? '2px solid var(--brand-accent-border)' : '2px solid var(--vscode-border-primary)',
                                  background: isSelected ? 'var(--brand-accent-bg)' : 'var(--vscode-bg-elevated)',
                                  cursor: 'pointer', textAlign: 'left',
                                  WebkitTapHighlightColor: 'transparent',
                                  boxShadow: 'var(--vscode-shadow-sm)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                {/* Header row: name + price */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                  <div>
                                    <p style={{ fontSize: 17, fontWeight: 800, color: isSelected ? 'var(--vscode-primary)' : 'var(--vscode-text-primary)', margin: 0, letterSpacing: '-0.01em' }}>
                                      {plan.name}
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                      <p style={{ fontSize: 12, color: isSelected ? 'var(--vscode-text-secondary)' : 'var(--vscode-text-secondary)', margin: 0 }}>
                                        {subtitle}
                                      </p>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 3,
                                        fontSize: 10, fontWeight: 700,
                                        padding: '2px 7px', borderRadius: 20,
                                        background: isSelected ? 'rgba(255,255,255,0.18)' : plan.autoRenew ? 'rgba(34,197,94,0.12)' : 'rgba(0,0,0,0.06)',
                                        color: isSelected ? 'rgba(255,255,255,0.9)' : plan.autoRenew ? '#16a34a' : 'var(--vscode-text-tertiary)',
                                      }}>
                                        {plan.autoRenew
                                          ? <><RefreshCw size={9} strokeWidth={2.5} />auto-renew</>
                                          : <><Clock size={9} strokeWidth={2.5} />one-time</>}
                                      </span>
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                                    <p style={{ fontSize: 28, fontWeight: 900, color: 'var(--vscode-text-primary)', margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>
                                      {formatCurrency(plan.id)}
                                    </p>
                                    {gstRatePct > 0 && (
                                      <p style={{ fontSize: 10, color: 'var(--vscode-text-tertiary)', margin: '3px 0 0', fontWeight: 600 }}>
                                        + GST ({gstRatePct}%) = {fmtINR(cardTotal)}
                                      </p>
                                    )}
                                    <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', margin: '2px 0 0' }}>
                                      {plan.periodLabel}
                                    </p>
                                  </div>
                                </div>
                                {/* Divider */}
                                <div style={{ height: 1, background: 'var(--vscode-border-primary)', marginBottom: 12 }} />
                                {/* Feature list */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {cardFeatures.map((f) => (
                                    <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <CheckCircle
                                        size={14}
                                        strokeWidth={2.5}
                                        style={{ flexShrink: 0, color: f.ok ? 'var(--vscode-success)' : 'var(--vscode-border-primary)' }}
                                      />
                                      <span style={{ fontSize: 13, color: f.ok ? 'var(--vscode-text-primary)' : 'var(--vscode-text-tertiary)' }}>
                                        {f.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                {/* Selected indicator: gradient background + white text is already clear — no overlapping badge needed */}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      {/* CTA */}
                      <div style={{ marginTop: 20 }}>
                        <button
                          type="button"
                          onClick={handleProceedToPayment}
                          disabled={!selectedPlan}
                          style={{
                            width: '100%', height: 52, borderRadius: 16, border: 'none',
                            background: selectedPlan ? 'var(--brand-cta-bg)' : 'var(--vscode-border-primary)',
                            color: 'var(--brand-cta-text)',
                            fontSize: 16, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            cursor: selectedPlan ? 'pointer' : 'default',
                            boxShadow: selectedPlan ? 'var(--brand-cta-shadow)' : 'none',
                            WebkitTapHighlightColor: 'transparent',
                            transition: 'all 0.15s',
                            opacity: selectedPlan ? 1 : 0.5,
                          }}
                        >
                          {selectedPlan
                            ? <><span>{selectedPlan.name} · {fmtINR(calcGST(selectedPlan.price).total)}{gstRatePct > 0 ? ` incl. ${gstRatePct}% GST` : ''}</span><ArrowRight size={18} strokeWidth={2.5} /></>
                            : <span>Select a plan above</span>
                          }
                        </button>
                        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--vscode-text-tertiary)', marginTop: 8 }}>
                          {selectedPlan ? 'Tap to proceed to payment' : 'Choose a plan to continue'}
                        </p>
                      </div>
                    </div>
                )}

                {/* Step 2: Payment (native only — web uses single-page checkout) */}
                {step === 'payment-method' && selectedPlan && isNative && (
                    /* ── Native: clean order-summary + confirm + pay ── */
                    (() => {
                      const { gst, total } = calcGST(selectedPlan.price);
                      return (
                        <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

                          {/* Order summary card */}
                          <div style={{ background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)', borderRadius: 20, overflow: 'hidden' }}>
                            {/* Plan identity header */}
                            <div style={{ padding: '16px 18px 14px', borderBottom: '1px solid var(--vscode-border-primary)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--vscode-primary)' }}>
                                    <Zap size={16} strokeWidth={2.2} />
                                  </div>
                                  <div>
                                    <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--vscode-text-primary)', margin: 0, letterSpacing: '-0.01em' }}>{selectedPlan.name}</p>
                                    <p style={{ fontSize: 12, color: 'var(--vscode-text-secondary)', margin: '2px 0 0' }}>
                                      {selectedPlan.autoRenew ? `Auto-renews every ${selectedPlan.period} · Cancel anytime` : 'One-time charge · No auto-renewal'}
                                    </p>
                                  </div>
                                </div>
                                <p style={{ fontSize: 22, fontWeight: 900, color: 'var(--vscode-text-primary)', margin: 0, letterSpacing: '-0.02em', flexShrink: 0 }}>{formatCurrency(selectedPlan.id)}</p>
                              </div>
                            </div>
                            {/* Price breakdown */}
                            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 14, color: 'var(--vscode-text-secondary)' }}>Subtotal</span>
                                <span style={{ fontSize: 14, color: 'var(--vscode-text-primary)', fontWeight: 600 }}>{fmtINR(selectedPlan.price)}</span>
                              </div>
                              {gstRatePct > 0 ? (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: 14, color: 'var(--vscode-text-secondary)', fontWeight: 500 }}>GST ({gstRatePct}%)</span>
                                  <span style={{ fontSize: 14, color: 'var(--vscode-text-secondary)', fontWeight: 600 }}>+ {fmtINR(gst)}</span>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)' }}>Tax (export — zero rated)</span>
                                  <span style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)' }}>₹0.00</span>
                                </div>
                              )}
                              <div style={{ height: 1, background: 'var(--vscode-border-primary)' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--vscode-text-primary)' }}>Total</span>
                                <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--vscode-primary)', letterSpacing: '-0.02em' }}>{fmtINR(total)}</span>
                              </div>
                              {/* Credits included */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 2 }}>
                                <CheckCircle size={14} color="var(--vscode-success)" strokeWidth={2.5} />
                                <span style={{ fontSize: 13, color: 'var(--vscode-text-secondary)' }}>
                                  <span style={{ fontWeight: 700, color: 'var(--vscode-primary)' }}>{getPackGrantCredits(selectedPlan)} optimizations</span> included
                                  {selectedPlan.bonusCredits ? ` + ${selectedPlan.bonusCredits} bonus` : ''}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Billing details (collapsible, for GST receipt) */}
                          <div style={{ background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)', borderRadius: 16, overflow: 'hidden' }}>
                            <button
                              type="button"
                              onClick={() => setBillingOpen((o) => !o)}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <CreditCard size={16} color="var(--vscode-primary)" strokeWidth={2} />
                                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--vscode-text-primary)' }}>Billing details</span>
                                <span style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)' }}>(for GST receipt)</span>
                              </div>
                              {billingOpen ? <ChevronUp size={16} color="var(--vscode-text-tertiary)" /> : <ChevronDown size={16} color="var(--vscode-text-tertiary)" />}
                            </button>
                            {billingOpen && (
                              <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--vscode-border-primary)' }}>
                                <p style={{ fontSize: 12, color: 'var(--vscode-text-tertiary)', marginTop: 10, marginBottom: 2 }}>Pre-filled from your profile. Edit if needed.</p>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                  {[
                                    { id: 'b-fn', label: 'First Name', key: 'firstName' as const },
                                    { id: 'b-ln', label: 'Last Name',  key: 'lastName'  as const },
                                  ].map(({ id, label, key }) => (
                                    <div key={id}>
                                      <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</label>
                                      <input id={id} type="text" value={userData[key]} onChange={(e) => updateBilling(key, e.target.value)} style={{ width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-primary)', color: 'var(--vscode-text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
                                    </div>
                                  ))}
                                </div>
                                {[
                                  { id: 'b-addr', label: 'Street Address', key: 'billingAddress' as const },
                                ].map(({ id, label, key }) => (
                                  <div key={id}>
                                    <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</label>
                                    <input id={id} type="text" value={userData[key]} onChange={(e) => updateBilling(key, e.target.value)} style={{ width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-primary)', color: 'var(--vscode-text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
                                  </div>
                                ))}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                  {[
                                    { id: 'b-city', label: 'City',    key: 'city'    as const },
                                    { id: 'b-state', label: 'State',  key: 'state'   as const },
                                    { id: 'b-ctr', label: 'Country',  key: 'country' as const },
                                    { id: 'b-zip', label: 'PIN Code', key: 'zipCode' as const },
                                  ].map(({ id, label, key }) => (
                                    <div key={id}>
                                      <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</label>
                                      <input id={id} type="text" value={userData[key]} onChange={(e) => updateBilling(key, e.target.value)} style={{ width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-primary)', color: 'var(--vscode-text-primary)', fontSize: 14, boxSizing: 'border-box' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Confirm + Pay */}
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '14px 16px', borderRadius: 16, border: `1.5px solid ${confirmPurchase ? 'var(--brand-accent-border)' : 'var(--vscode-border-primary)'}`, background: confirmPurchase ? 'var(--brand-accent-bg)' : 'var(--vscode-bg-elevated)', WebkitTapHighlightColor: 'transparent', transition: 'all 0.15s' }}>
                            <input
                              type="checkbox"
                              checked={confirmPurchase}
                              onChange={(e) => setConfirmPurchase(e.target.checked)}
                              style={{ width: 18, height: 18, borderRadius: 5, marginTop: 1, accentColor: 'var(--vscode-primary)', flexShrink: 0, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--vscode-text-primary)' }}>
                              Yes, I am purchasing <strong>{selectedPlan.name}</strong> for{' '}
                              <strong style={{ color: 'var(--vscode-primary)' }}>{fmtINR(total)}</strong>
                              {gstRatePct > 0 && <span style={{ fontSize: 12, color: 'var(--vscode-text-tertiary)' }}> (incl. {gstRatePct}% GST)</span>}
                            </span>
                          </label>

                          <button
                            type="button"
                            onClick={handlePayAndUpgrade}
                            disabled={!confirmPurchase}
                            style={{
                              width: '100%', height: 52, borderRadius: 16, border: 'none',
                              background: confirmPurchase ? 'var(--brand-cta-bg)' : 'var(--vscode-border-primary)',
                              color: 'var(--brand-cta-text)',
                              fontSize: 16, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                              cursor: confirmPurchase ? 'pointer' : 'default',
                              boxShadow: confirmPurchase ? 'var(--brand-cta-shadow)' : 'none',
                              opacity: confirmPurchase ? 1 : 0.45,
                              WebkitTapHighlightColor: 'transparent',
                              transition: 'all 0.15s',
                            }}
                          >
                            <span>Pay {fmtINR(total)}</span>
                            <ArrowRight size={18} strokeWidth={2.5} />
                          </button>

                          <button
                            type="button"
                            onClick={() => { setStep('select-plan'); setConfirmPurchase(false); }}
                            style={{ width: '100%', minHeight: 44, background: 'none', border: 'none', fontSize: 14, color: 'var(--vscode-primary)', fontWeight: 600, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                          >
                            ← Back to plans
                          </button>

                          {/* Security note */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 14, background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)' }}>
                            <Lock size={14} style={{ flexShrink: 0, color: 'var(--vscode-primary)' }} strokeWidth={2} />
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--vscode-text-primary)', margin: 0 }}>Secure and encrypted payment</p>
                              <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)', margin: '2px 0 0' }}>Cashfree · Card, UPI, NetBanking</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                )}

                {/* Step 3: Processing – compact layout */}
                {step === 'processing' && (
                  <div className="text-center py-3 sm:py-4">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-3">
                      <LoadingSpinner size="md" text="" />
                      <div className="text-left sm:text-center">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{t('creditsPurchaseModal.processingPayment')}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('creditsPurchaseModal.redirectingPleaseWait')}</p>
                      </div>
                    </div>
                    {selectedPlan && (
                      <div className="mx-auto max-w-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-left space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.plan')}</span>
                          <span className="font-medium text-gray-900 dark:text-white">{selectedPlan.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.amount')}</span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {fmtINR(calcGST(selectedPlan.price).total)}
                            {gstRatePct > 0 && (
                              <span className="text-xs text-amber-500 ml-1">
                                {t('creditsPurchaseModal.inclTaxShort')}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.creditsLabel')}</span>
                          <span className="font-semibold text-[var(--vscode-primary)]">{getPackGrantCredits(selectedPlan)}</span>
                        </div>
                        {transactionId && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.orderId')}</span>
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate max-w-[100px]" title={transactionId}>{transactionId.slice(0, 8)}…</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.elapsed')}</span>
                          <span className="font-mono tabular-nums text-gray-900 dark:text-white">
                            {Math.floor(processingElapsedSeconds / 60)}:{(processingElapsedSeconds % 60).toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 4: Success */}
                {step === 'success' && selectedPlan && (
                  <div className="text-center py-6 max-w-sm mx-auto px-2">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }} className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--vscode-primary)]/10 dark:bg-[var(--vscode-primary)]/20 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-[var(--vscode-primary)]" aria-hidden />
                    </motion.div>
                    <h3 className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      <CheckCircle className="w-6 h-6 text-[var(--vscode-primary)] shrink-0" aria-hidden />
                      {t('creditsPurchaseModal.paymentSuccessful')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('creditsPurchaseModal.creditsAddedMessage')}</p>
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-5 text-left text-sm">
                      <div className="flex justify-between items-center mb-1"><span className="text-gray-500 dark:text-gray-400">{t('creditsPurchaseModal.amount')}</span><span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(selectedPlan.id)}</span></div>
                      <div className="flex justify-between items-center gap-2"><span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400"><Coins className="w-4 h-4 text-[var(--vscode-primary)] shrink-0" aria-hidden />{t('creditsPurchaseModal.creditsAdded')}</span><span className="font-semibold text-[var(--vscode-primary)]">{getPackGrantCredits(selectedPlan)}</span></div>
                    </div>
                    <button type="button" onClick={handleClose} className="w-full min-h-[44px] hover:opacity-90 font-semibold rounded-xl flex items-center justify-center gap-2 touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
                      <Coins className="w-4 h-4 shrink-0" aria-hidden />
                      <span>{t('creditsPurchaseModal.startOptimizing')}</span>
                      <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                    </button>
                  </div>
                )}

                {/* Step 5: Error */}
                {step === 'error' && (
                  <div className="text-center py-6 max-w-sm mx-auto px-2">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }} className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden />
                    </motion.div>
                    <h3 className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      <CreditCard className="w-6 h-6 text-red-500 dark:text-red-400 shrink-0" aria-hidden />
                      {t('creditsPurchaseModal.paymentFailed')}
                    </h3>
                    <p className="text-red-600 dark:text-red-400 mb-5 text-sm px-2">{error || t('creditsPurchaseModal.paymentFailedMessage')}</p>
                    <div className="space-y-3">
                      <button type="button" onClick={handleTryAgain} className="w-full min-h-[44px] hover:opacity-90 font-semibold rounded-xl flex items-center justify-center gap-2 touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]" style={{ background: 'var(--brand-cta-bg)' }}>
                        <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                        {t('creditsPurchaseModal.tryAgain')}
                      </button>
                      <button type="button" onClick={handleClose} className="w-full min-h-[44px] bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-2 touch-manipulation">
                        <X className="w-4 h-4 shrink-0" aria-hidden />
                        {t('creditsPurchaseModal.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(overlay, document.body);
  }
  return null;
};

export default React.memo(CreditPurchaseModal);
